/**
 * Feb 2026 · P0 — Central Online Discount hook.
 *
 * Every place on the public site that renders "Save X%", the
 * "ONLINE PREPAID" pill, or the strike-through retail price MUST read
 * from this hook — never from a hardcoded 20 / "Save 20%" string.
 *
 * When the admin disables the discount at /admin/settings/online-discounts:
 *   - `enabled` becomes false
 *   - `savingsText` returns ""
 *   - `showPill` and `showStrikethrough` return false
 *   - `priceFor(testId)` returns the retail price
 *
 * Cache: the /api/pricing snapshot changes rarely so we fetch once per
 * mount and re-use the value. Consumers that need a fresh read after a
 * settings change (like the admin preview page) call `usePublicPricing()`
 * in their own component tree.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";

const DEFAULT_SETTINGS = {
  enabled: true,
  percentage: 20,
  label_pill: "ONLINE PREPAID",
  label_savings_template: "Save {pct}%",
  show_strikethrough: true,
};

export function usePublicPricing() {
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");

  useEffect(() => {
    let alive = true;
    // Feb 2026 (Greg Pearson follow-up) — bust every layer of cache so
    // toggling the admin discount switch shows through in other tabs
    // without a hard refresh: `_ts` param defeats HTTP intermediary
    // caches, no-store defeats the browser, and we re-fetch on focus.
    const load = () => {
      api.get(`/pricing?_ts=${Date.now()}`, {
        headers: { "Cache-Control": "no-cache", "Pragma": "no-cache" },
      })
        .then(({ data }) => { if (alive) setSnapshot(data); })
        .catch((e) => { if (alive) setError(e?.message || "pricing_load_failed"); })
        .finally(() => { if (alive) setLoading(false); });
    };
    load();

    // Re-fetch whenever the tab regains focus (admin toggled the
    // switch in another tab and came back) or the browser fires
    // `visibilitychange` (mobile app-switch).
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      alive = false;
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, []);

  const discount = snapshot?.online_discount || DEFAULT_SETTINGS;
  const enabled  = !!discount.enabled;
  const pct      = Number(discount.percentage || 0);
  const savingsText = enabled && pct > 0
    ? (discount.label_savings_template || DEFAULT_SETTINGS.label_savings_template).replace("{pct}", String(pct))
    : "";

  const priceFor = (testId) => {
    const t = (snapshot?.tests || []).find((r) => r.id === testId || r.slug === testId);
    if (!t) return null;
    // When disabled, callers should render retail only. We still return
    // both so the caller can decide, but `effective` collapses to retail.
    return {
      test_id: t.id || t.slug,
      name:    t.name,
      retail:  t.retail,
      online_prepaid: enabled ? t.online_prepaid : t.retail,
      effective:      enabled ? t.online_prepaid : t.retail,
    };
  };

  return {
    loading,
    error,
    snapshot,
    tests: snapshot?.tests || [],
    // Discount surface — every UI decision derives from these:
    enabled,
    pct,
    pill:              enabled && discount.label_pill  ? String(discount.label_pill) : "",
    savingsText,
    showPill:          enabled && !!discount.label_pill,
    showStrikethrough: enabled && !!discount.show_strikethrough,
    priceFor,
  };
}
